import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { Select2ScrollEvent, Select2SearchEvent } from 'ng-select2-component';
import { IBase, IClient } from 'src/app/models';
import { ExpeditionStatus, ExpeditionType, InputType } from 'src/app/models/enums';
import { RouteService, StorageService, TripsService, UsersService, UtilsService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { ServiceInfoService } from 'src/app/services/service-info/service-info.service';
import { TooltipPosition } from 'src/app/shared/tooltip/tooltip-position';
import { images } from 'src/images';
import {
  ITripsFilters,
  TRIP_FILTER_KEYS,
  deserializeTripsFilters,
  hasAnyFilterKey,
  parseTripsFiltersFromStorage,
} from 'src/app/filters';

@Component({
    selector: 'app-trip-filter',
    templateUrl: './filter.component.html',
    styleUrl: './filter.component.scss',
    standalone: false
})
export class TripFilterComponent implements OnChanges, OnDestroy {
  private queryParamsSub?: Subscription;
  private renderListener: () => void;
  private servicesLoadedForTownsKey: string | null = null;
  private initialized = false;
  private servicesRequestId = 0;
  private routesRequestId = 0;
  public expeditionStatus: any[] = [];
  public expeditionStatusCopy: any[] = [];
  public expeditionTypes: any[] = [];
  public ExpeditionStatus = ExpeditionStatus;
  public ExpeditionTypes = ExpeditionType;
  public closedStatusSelected = false;

  public clients: unknown[] = [];
  public selectedValuesClients: IBase[] = [];
  public operators: unknown[] = [];
  public selectedValuesOperators: IBase[] = [];

  public servicesPage = 1;
  public servicesSearchValue = '';
  public servicesElement: any = {
    id: 'tripServices',
    value: [],
    edit: {
      editable: true,
      inputType: InputType.MULTISELECT_SCROLL,
      values: [],
      valueToChange: 'services',
      customSearchEnabled: true,
      placeholder: '-'
    }
  };

  public routesPage = 1;
  public routesSearchValue = '';
  public routesElement: any = {
    id: 'tripRoutes',
    value: [],
    edit: {
      editable: true,
      inputType: InputType.MULTISELECT_SCROLL,
      values: [],
      valueToChange: 'routes',
      customSearchEnabled: true,
      disabled: true,
      placeholder: '-'
    }
  };

  public images = images;
  public TooltipPosition = TooltipPosition;

  public filterSelected = 'all';
  public filters: any[] = [];
  public showClients: boolean = false;

  @Output() filter = new EventEmitter();
  @Output() closeFilter = new EventEmitter();

  @Input() componentOpen: string;
  @Input() isFilterOpen: boolean = false;

  @ViewChild('select') select: ElementRef;

  constructor(
    renderer: Renderer2,
    public literalService: LiteralService,
    public tripsService: TripsService,
    public utilsService: UtilsService,
    public usersService: UsersService,
    public storageService: StorageService,
    public serviceInfoService: ServiceInfoService,
    public routeService: RouteService,
    private activatedRoute: ActivatedRoute,
  ) {
    this.expeditionStatus.push({value: 'Open', checked: true });
    this.expeditionStatus.push({value: 'Ready', checked: true });
    this.expeditionStatus.push({value: 'Ongoing', checked: true });
    
    (Object.keys(ExpeditionType) as Array<keyof typeof ExpeditionType>).map(
      (expeditionTypes: keyof typeof ExpeditionType) => {
        if (expeditionTypes) this.expeditionTypes.push({ value: expeditionTypes, checked: false });
      },
    );

    // Renderer2 window listeners outlive the component, so keep the handle to detach on destroy.
    this.renderListener = renderer.listen('window', 'click', (e: Event) => {
      if (!this.isFilterOpen) return;
      const modal = document.getElementById('filterContainer')!;
      const componentOpen = document.getElementById(this.componentOpen)!;
      const close = utilsService.closeCustomModals(e, modal, componentOpen);
      if (close) {
        this.closeFilter.emit();
        this.initFilters();
        this.select.nativeElement.checked = false;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['isFilterOpen']?.currentValue) return;
    if (!this.initialized) {
      this.initialized = true;
      this.lazyInit();
    } else {
      // The panel is never destroyed between opens, so state must be re-synced here
      // or a reopen shows whatever was left over from the previous close (e.g. an
      // emptied routes list).
      this.initFilters();
    }
  }

  private async lazyInit(): Promise<void> {
    const { clients } = await this.usersService.getPossibleClientTowns();
    clients.map((client: IClient) => {
      this.clients.push({
        id: client.id,
        code: client.code,
        name: client.name,
      });
    });

    const operators = await this.usersService.getOperators();
    operators.map((operator: IBase) => {
      this.operators.push({ id: operator.id, code: operator.name, name: operator.name });
    });

    this.showClients = clients.length > 1;

    // initFilters() applies URL/storage state and loads services for the resolved towns.
    this.initFilters();
    // Only while open: a closed panel is re-synced by ngOnChanges on the next open.
    this.queryParamsSub = this.activatedRoute.queryParamMap.subscribe(() => {
      if (this.isFilterOpen) this.initFilters();
    });
  }

  ngOnDestroy(): void {
    this.queryParamsSub?.unsubscribe();
    this.renderListener?.();
  }

  async loadServices(showMore = false) {
    if (!showMore) this.servicesPage = 1;
    const requestId = ++this.servicesRequestId;
    const areasJson = await this.storageService.getArea();
    const towns = areasJson ? JSON.parse(areasJson) : [];
    const townParams = towns.length > 0 ? towns.map((t: any) => ({ id: t.id })) : undefined;
    const result = await this.serviceInfoService.getServiceInfosSearch(10, this.servicesPage, townParams, this.servicesSearchValue || undefined);
    if (requestId !== this.servicesRequestId) return;
    const content = (result as any).content || result;
    const newValues = (content as any[]).map((s: any) => ({
      id: s.id,
      value: { id: s.id, tag: s.tag, name: s.name },
      label: this.serviceLabel(s)
    }));
    this.syncOptions(this.servicesElement, newValues, showMore, (s: any) => this.serviceLabel(s));
  }

  private serviceLabel(service: any): string {
    return service.name ? `${service.tag}: ${service.name}` : service.tag;
  }

  // select2 resolves the selection against the option list by object identity, so each selected
  // item must be the very instance its option carries, and must stay in the list across reloads.
  private syncOptions(element: any, loaded: any[], showMore: boolean, label: (item: any) => string) {
    const options = showMore ? [...element.edit.values, ...loaded] : loaded;
    const byId = new Map<number, any>(options.map((option: any) => [option.id, option]));
    const selected = (element.value as any[]).map((item: any) => byId.get(item.id)?.value ?? item);
    const orphans = selected
      .filter((item: any) => !byId.has(item.id))
      .map((item: any) => ({ id: item.id, value: item, label: label(item) }));
    element.value = selected;
    element.edit.values = [...orphans, ...byId.values()];
  }

  private async recomputeTownsKeyAndMaybeLoadServices(onDone: () => void): Promise<void> {
    const areasJson = await this.storageService.getArea();
    const towns = areasJson ? JSON.parse(areasJson) : [];
    const townsKey = towns.length > 0
      ? towns.map((t: any) => t.id).sort().join(',')
      : '<all>';
    if (townsKey === this.servicesLoadedForTownsKey) {
      onDone();
      return;
    }
    this.servicesLoadedForTownsKey = townsKey;
    await this.loadServices();
    onDone();
  }

  async loadRoutes(showMore = false) {
    if (!showMore) this.routesPage = 1;
    const requestId = ++this.routesRequestId;
    const serviceIds = (this.servicesElement.value as any[]).map((s: any) => s.id);
    if (serviceIds.length === 0) {
      this.routesElement.edit.values = [];
      return;
    }
    const result = await this.routeService.getRoutesSearch(serviceIds, this.routesPage, 10, this.routesSearchValue || undefined);
    if (requestId !== this.routesRequestId) return;
    const content = (result as any).content || result;
    const newValues = (content as any[]).map((r: any) => ({
      id: r.id,
      value: { id: r.id, name: r.name },
      label: r.name
    }));
    this.syncOptions(this.routesElement, newValues, showMore, (r: any) => r.name);
  }

  onServicesChange(value: any) {
    // select2 echoes an update whenever the bound value is replaced (e.g. after a reload),
    // and acting on it would wipe the routes restored alongside the services.
    if (this.sameSelection(value, this.servicesElement.value)) return;
    this.servicesElement.value = value ?? [];
    this.routesElement.value = [];
    this.routesElement.edit.values = [];
    this.routesPage = 1;
    this.routesSearchValue = '';
    this.routesElement.edit.disabled = !value || value.length === 0;
    if (value && value.length > 0) {
      this.loadRoutes();
    }
  }

  onRoutesChange(value: any) {
    this.routesElement.value = value ?? [];
  }

  private sameSelection(a: any[] | null | undefined, b: any[] | null | undefined): boolean {
    const first = a ?? [];
    const second = b ?? [];
    return first.length === second.length
      && first.every((item: any) => second.some((other: any) => other.id === item.id));
  }

  async onScroll(event: Select2ScrollEvent, valueToChange: string) {
    if (event.way === 'down') {
      if (valueToChange === 'services') {
        this.servicesPage += 1;
        await this.loadServices(true);
      } else if (valueToChange === 'routes') {
        this.routesPage += 1;
        await this.loadRoutes(true);
      }
    }
  }

  async onSearch(event: Select2SearchEvent, valueToChange: string) {
    if (valueToChange === 'services') {
      this.servicesPage = 1;
      this.servicesSearchValue = event.search;
      await this.loadServices();
    } else if (valueToChange === 'routes') {
      this.routesPage = 1;
      this.routesSearchValue = event.search;
      await this.loadRoutes();
    }
  }

  async initFilters() {
    const filters = await this.resolveCurrentFilters();
    if (!filters) return;
    this.applyFiltersToView(filters);
  }

  private async resolveCurrentFilters(): Promise<ITripsFilters | null> {
    const params = this.activatedRoute.snapshot.queryParamMap;
    const storageFilters = parseTripsFiltersFromStorage(await this.storageService.getTripsFilters());
    if (hasAnyFilterKey(params, TRIP_FILTER_KEYS)) {
      return deserializeTripsFilters(params, storageFilters ?? undefined);
    }
    return storageFilters;
  }

  private applyFiltersToView(selectedFilters: ITripsFilters) {
    this.selectedValuesClients = selectedFilters.selectedClients;
    this.selectedValuesOperators = selectedFilters.selectedOperators;
    this.servicesElement.value = selectedFilters.selectedServices || [];
    this.routesElement.value = selectedFilters.selectedRoutes || [];

    this.expeditionStatus.map((status) => {
      status.checked = selectedFilters.selectedStatus.some((s: any) => s === status.value);
    });
    this.expeditionStatusCopy = this.expeditionStatus;
    this.closedStatusSelected = selectedFilters.selectedStatus.some((s: any) => s === ExpeditionStatus.CLOSED);

    this.expeditionTypes.map((type) => {
      type.checked = selectedFilters.selectedTypes.some((t: any) => t === type.value);
    });

    this.recomputeTownsKeyAndMaybeLoadServices(() => {
      this.routesElement.edit.disabled = this.servicesElement.value.length === 0;
      if (this.servicesElement.value.length > 0) {
        this.loadRoutes();
      }
    });
  }

  changeFilter(event: any) {
    this.filterSelected = event.target.value;
  };

  onChangeExclusiveStatus(type: string) {
    if (this.closedStatusSelected) {
      this.expeditionStatus = this.expeditionStatusCopy;
      this.expeditionStatusCopy = [];
      this.expeditionStatus.map((status) => status.checked = [ExpeditionStatus.OPEN, ExpeditionStatus.READY, ExpeditionStatus.ONGOING].includes(status.value));
    } else {
      this.expeditionStatusCopy = structuredClone(this.expeditionStatus);
      this.expeditionStatus.map((status) => status.checked = false);
    }
    if (type === 'closed') this.closedStatusSelected = !this.closedStatusSelected;
  }

  selectAll(event: any) {
    this.closedStatusSelected = false;
    this.expeditionStatus.map(
      (status: any) => (status.checked = event.currentTarget.checked
        ? true
        : [ExpeditionStatus.OPEN, ExpeditionStatus.READY, ExpeditionStatus.ONGOING].includes(status.value)),
    );
    this.expeditionTypes.map(
      (type: any) => (type.checked = event.currentTarget.checked),
    );
    if (event.currentTarget.checked) {
      this.clients.map((client: any) =>
        this.selectedValuesClients.push({ id: client.id, name: client.code }),
      );
      this.operators.map((operator: any) =>
        this.selectedValuesOperators.push({ id: operator.id, name: operator.code }),
      );
    } else {
      this.selectedValuesClients = [];
      this.selectedValuesOperators = [];    }
  };

  clearSelection() {
    this.expeditionStatus.map((status: any) => (status.checked = [ExpeditionStatus.OPEN, ExpeditionStatus.READY, ExpeditionStatus.ONGOING].includes(status.value)));
    this.expeditionTypes.map((type: any) => (type.checked = false));
    this.selectedValuesClients = [];
    this.selectedValuesOperators = [];
    this.servicesElement.value = [];
    this.servicesElement.edit.values = [];
    this.routesElement.value = [];
    this.routesElement.edit.values = [];
    this.routesElement.edit.disabled = true;
    this.servicesPage = 1;
    this.servicesSearchValue = '';
    this.routesPage = 1;
    this.routesSearchValue = '';
    this.select.nativeElement.checked = false;
    this.closedStatusSelected = false;
    this.expeditionStatusCopy = [];
    this.loadServices();
  };

  async onSubmit() {
    const selectedClients: IBase[] = [];
    this.selectedValuesClients.forEach((client: IBase) => {
      selectedClients.push(client);
    });

    const selectedOperators: IBase[] = [];
    this.selectedValuesOperators.forEach((operator: IBase) => {
      selectedOperators.push(operator);
    });

    const selectedServices: any[] = (this.servicesElement.value as any[]).map((s: any) => ({
      id: s.id,
      tag: s.tag,
      name: s.name
    }));

    const selectedRoutes: any[] = (this.routesElement.value as any[]).map((r: any) => ({
      id: r.id,
      name: r.name
    }));

    const selectedTypes: string[] = [];
    this.expeditionTypes.forEach((type: any) => {
      type.checked && selectedTypes.push(type.value);
    });

    const selectedStatus = [];
    if (this.closedStatusSelected) selectedStatus.push(ExpeditionStatus.CLOSED);
    else {
      this.expeditionStatus.forEach((status: any) => {
        if (status.checked) selectedStatus.push(status.value);
      });
    }

    this.storageService.setTripsFilters(JSON.stringify({
      selectedClients,
      selectedOperators,
      selectedServices,
      selectedRoutes,
      selectedTypes,
      selectedStatus
    }))

    this.filter.emit({selectedClients, selectedOperators, selectedServices, selectedRoutes, selectedTypes, selectedStatus});
  };
}
